// SINGLE SOURCE OF TRUTH for per-platform composing limits. Add a platform =
// one entry here and every consumer (caption counter, per-network glyphs,
// hashtag warnings, preview) picks it up automatically. charLimit = max caption
// characters; hashtagLimit = soft max hashtags (null = no meaningful cap);
// maxMedia = max images in one post (carousel/album cap) for the My-image path.
export const PLATFORM_LIMITS = {
  instagram: { charLimit: 2200, hashtagLimit: 30, maxMedia: 10 },
  x: { charLimit: 280, hashtagLimit: null, maxMedia: 4 },
  twitter: { charLimit: 280, hashtagLimit: null, maxMedia: 4 }, // alias of x — keep in sync
  bluesky: { charLimit: 300, hashtagLimit: null, maxMedia: 4 },
  threads: { charLimit: 500, hashtagLimit: null, maxMedia: 10 },
  linkedin: { charLimit: 3000, hashtagLimit: null, maxMedia: 9 },
  tiktok: { charLimit: 2200, hashtagLimit: null, maxMedia: 35 },
  telegram: { charLimit: 4096, hashtagLimit: null, maxMedia: 10 },
  facebook: { charLimit: 63206, hashtagLimit: null, maxMedia: 10 },
};

const DEFAULT_PLATFORM_LIMIT = { charLimit: 2200, hashtagLimit: null, maxMedia: 10 };

// Absolute hard ceiling regardless of platform (matches the backend
// providedMediaAssetIds cap). Keeps a runaway selection bounded.
export const MAX_MEDIA_HARD_CAP = 20;

export function getPlatformLimit(platform) {
  return PLATFORM_LIMITS[platform] || DEFAULT_PLATFORM_LIMIT;
}

export function getHashtagLimit(platform) {
  return getPlatformLimit(platform).hashtagLimit;
}

// Tightest (most restrictive) limits across a set of selected platform keys.
// Empty selection → the default char limit, no hashtag cap.
export function tightestLimits(platforms) {
  const list = Array.isArray(platforms) ? platforms : [];
  if (!list.length) return { charLimit: DEFAULT_PLATFORM_LIMIT.charLimit, hashtagLimit: null };
  return list.reduce(
    (acc, p) => {
      const { charLimit, hashtagLimit } = getPlatformLimit(p);
      return {
        charLimit: Math.min(acc.charLimit, charLimit),
        hashtagLimit: hashtagLimit == null ? acc.hashtagLimit : Math.min(acc.hashtagLimit ?? Infinity, hashtagLimit),
      };
    },
    { charLimit: Infinity, hashtagLimit: Infinity }
  );
}

// Tightest media cap across the selected platforms + which platform constrains
// it (for "up to N — limited by X"). Empty selection → the default cap. Always
// bounded by the hard ceiling.
export function tightestMaxMedia(platforms) {
  const list = Array.isArray(platforms) ? platforms.filter(Boolean) : [];
  if (!list.length) return { max: DEFAULT_PLATFORM_LIMIT.maxMedia, platform: null };
  let max = Infinity;
  let platform = null;
  for (const p of list) {
    const m = (PLATFORM_LIMITS[p] || DEFAULT_PLATFORM_LIMIT).maxMedia ?? DEFAULT_PLATFORM_LIMIT.maxMedia;
    if (m < max) { max = m; platform = p; }
  }
  return { max: Math.min(max, MAX_MEDIA_HARD_CAP), platform };
}

export function pickPlatformText(post, platform) {
  if (!post) return '';
  const platformVersions = post.platformVersions || {};
  const aliasKey = platform === 'x' ? 'twitter' : platform === 'twitter' ? 'x' : null;
  const direct = platformVersions[platform];
  if (direct && typeof direct === 'string' && direct.trim()) return direct;
  if (aliasKey) {
    const aliased = platformVersions[aliasKey];
    if (aliased && typeof aliased === 'string' && aliased.trim()) return aliased;
  }
  return [post.caption, post.body, post.hook].find(value => typeof value === 'string' && value.trim()) || '';
}

export function joinHashtags(hashtags) {
  if (!Array.isArray(hashtags) || !hashtags.length) return '';
  return hashtags
    .map(tag => (typeof tag === 'string' ? tag.trim() : ''))
    .filter(Boolean)
    .map(tag => (tag.startsWith('#') ? tag : `#${tag}`))
    .join(' ');
}

export function platformLabel(platform) {
  const labels = {
    instagram: 'Instagram',
    threads: 'Threads',
    linkedin: 'LinkedIn',
    x: 'X',
    twitter: 'X',
    tiktok: 'TikTok',
    facebook: 'Facebook',
    telegram: 'Telegram',
    bluesky: 'Bluesky',
  };
  return labels[platform] || platform;
}

export function getCharacterLimit(platform) {
  return getPlatformLimit(platform).charLimit;
}
